import { createRouter } from "@tanstack/react-router";
import { CustomErrorComponent } from "./components/custom/router/custom-error";
import { NotFoundRoute } from "./components/custom/router/not-found-route";
import {
  asPreload,
  freshLoadTarget,
  leaveFor,
  onBuildStale,
} from "./lib/chunk-reload";
import { queryClient } from "./lib/query-client";
import { routeTree } from "./routeTree.gen";

// Constants for timing configurations
const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const DEFAULT_STALE_TIME_SECONDS = 3;
const PRELOAD_GC_TIME_HOURS = 1;

// Create a new router instance
export const router = createRouter({
  routeTree,
  basepath: "/",
  defaultNotFoundComponent: NotFoundRoute,
  defaultErrorComponent: CustomErrorComponent,
  // Show the pending UI immediately instead of after the 1000ms default. On a
  // hard load there is no previous screen to hold, so during the pending window
  // the router renders nothing — a blank dark screen. Context-resolving redirect
  // routes that have no component of their own (`/`, `/orgs/$id`,
  // `/orgs/$id/projects/$id/`) spend that whole window in their async beforeLoad
  // (org + project + eligibility fetches, then a redirect), so the default debounce
  // means the screen stays black until everything resolves. 0 surfaces the loader
  // at once; after the first load the delay is raised (see NAVIGATION_PENDING_MS).
  defaultPendingMs: 0,
  // Fallback for routes without a pendingComponent of their own. Most render
  // inside the dashboard layout's content area while their code chunk or data
  // loads, then show their own skeleton, so this leaves the area empty rather
  // than adding a second, different placeholder. Routes above the layout use
  // AppShellSkeleton instead.
  defaultPendingComponent: () => null,
  defaultPreload: "intent",
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  defaultStaleTime: MILLISECONDS_PER_SECOND * DEFAULT_STALE_TIME_SECONDS,
  defaultPreloadGcTime:
    MILLISECONDS_PER_SECOND *
    SECONDS_PER_MINUTE *
    MINUTES_PER_HOUR *
    PRELOAD_GC_TIME_HOURS,
  context: {
    queryClient,
  },
});

const preloadRoute = router.preloadRoute;
router.preloadRoute = (options) => asPreload(() => preloadRoute(options));
const loadFresh = (href: string | null) => {
  if (href) {
    leaveFor(href);
  }
};
// After a preload finds the build replaced, the next page change, or one
// already under way, becomes a full page load of its destination on the new
// build.
onBuildStale(() => {
  if (router.state.status === "pending") {
    loadFresh(
      freshLoadTarget(router.state.resolvedLocation, router.state.location)
    );
  }
});
router.subscribe("onBeforeNavigate", ({ fromLocation, toLocation }) =>
  loadFresh(freshLoadTarget(fromLocation, toLocation))
);

// Once the first page is up, an in-app navigation has a screen to hold, so it
// keeps that screen until the next page is ready rather than blanking the
// content area. Shorter delays trip the router's 500ms pendingMinMs, so a
// navigation that would finish in a few hundred milliseconds instead shows the
// pending UI for at least half a second.
const NAVIGATION_PENDING_MS = 1000;
const stopWatchingFirstLoad = router.subscribe("onResolved", () => {
  stopWatchingFirstLoad();
  router.update({ ...router.options, defaultPendingMs: NAVIGATION_PENDING_MS });
});

type Router = typeof router;

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  // biome-ignore lint/style/useConsistentTypeDefinitions: This is ok
  interface Register {
    router: Router;
  }
}
